import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowUpRight,
  Cpu,
  LineChart,
  Network,
  Activity,
  Zap,
  ShieldCheck,
  Database,
  Cable,
  Radio,
  BatteryCharging,
  Gauge,
  LayoutDashboard,
  Bell,
  Leaf,
  FileBarChart2,
  Building2,
  Fan,
  Lightbulb,
} from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { useLanguage } from "../context/LanguageContext";

// DICIONÁRIO COMPLETO DE TRADUÇÕES
const TRANSLATIONS = {
  en: {
    heroEyebrow: "Products",
    heroTitle: <><span className="text-[#F68A31]">WSBP</span> Integrated Stack</>,
    heroSubtitle: "PPC, SCADA, BESS, JANUS and BMS in a single engineering stack — coordinating PV plants, hybrid sites, storage systems, substations and buildings in real time.",
    btnDemo: "Request a demo",
    btnTalkEng: "Talk to engineering",
    btnAccess: "Request access",
    ppcTitle: "Power Plant Controller",
    ppcDesc: "The central real-time coordination platform. WSBP's PPC strictly enforces the setpoints defined by grid operators, ensuring stable active power, reactive power, voltage and frequency.",
    ppcFeatures: [
      { icon: Zap, title: "P / f  &  Q / U Control", desc: "Stable active power, frequency, reactive power and voltage in line with the operator's setpoints." },
      { icon: ShieldCheck, title: "Grid operators", desc: "Strict compliance with grid operator requirements across Portugal and Europe." },
      { icon: Network, title: "Multi-protocol", desc: "Modbus TCP/RTU, IEC 61850, IEC 60870, DNP3, OPC UA, among others." },
      { icon: Gauge, title: "Low response time", desc: "Deterministic control loop with reduced latency, ensuring quick response to setpoint and grid variations." },
    ],
    scadaTitle: "SCADA Systems",
    scadaDesc: "Custom-built supervision solutions, project by project. Full monitoring of field equipment (inverters, trackers, weather stations), alarm management and analysis of critical KPIs such as Performance Ratio, capacity factor and plant availability.",
    scadaFeatures: [
      { icon: Activity, title: "Full monitoring", desc: "Inverters, trackers, weather stations and more." },
      { icon: Database, title: "Critical KPIs", desc: "Performance Ratio, capacity factor and plant availability in real time." },
      { icon: Radio, title: "Alarm management", desc: "Event correlation, priorities and notifications via email and SMS." },
      { icon: Cable, title: "Custom-built", desc: "Supervision solutions designed for the specific requirements of each project." },
    ],
    bessTitle: "Energy Storage",
    bessDesc: "We are at the forefront of battery energy storage system (BESS) integration — we delivered the first stand-alone battery cluster in Portugal. Our SCADA and control solutions provide complete power-flow management, detailed monitoring of the State of Charge (SoC) and safe interaction with the electricity market in hybrid plants.",
    bessFeatures: [
      { icon: BatteryCharging, title: "SoC management", desc: "Detailed monitoring of State of Charge, temperature, voltage and health for each string." },
      { icon: Gauge, title: "Power flow", desc: "Full charge/discharge control coordinated with PPC and EMS." },
      { icon: Cpu, title: "Electricity market", desc: "Integration with spot market and ancillary services (frequency, reserves)." },
      { icon: ShieldCheck, title: "Safe operation", desc: "Protection and redundancy strategies to ensure cutting-edge integration." },
    ],
    janusTitle: "JANUS",
    janusDesc: "A web platform for real-time monitoring and analysis, focused on energy efficiency and sustainability. JANUS tracks distributed networks of facilities, integrates environmental and energy sensor data and produces automated reporting that supports operational decisions and long-term maintenance strategies.",
    janusFeatures: [
      { icon: LayoutDashboard, title: "Web platform", desc: "Browser-based dashboards for distributed sites — no local install required." },
      { icon: Leaf, title: "Energy efficiency", desc: "Designed around energy efficiency and sustainability KPIs." },
      { icon: Bell, title: "Sensors & alerts", desc: "Integrates environmental and energy sensors with smart alerting." },
      { icon: FileBarChart2, title: "Automated reporting", desc: "Recurring reports and analytics for operations and management teams." },
    ],
    bmsTitle: "Building Management System",
    bmsDesc: "Centralised technical management (BMS) — where WSBP began. We design and integrate systems that supervise and control HVAC, lighting, energy and technical alarms in buildings, with projects delivered in airports, hospitals, hotels, schools and municipal facilities across Portugal, Cape Verde and the United Arab Emirates.",
    bmsFeatures: [
      { icon: Fan, title: "HVAC control", desc: "Supervision and control of ventilation, air conditioning and air quality systems." },
      { icon: Lightbulb, title: "Lighting & energy", desc: "Lighting management, energy metering and consumption optimisation." },
      { icon: LayoutDashboard, title: "Centralised supervision", desc: "All building systems integrated and operated from a single platform." },
      { icon: Bell, title: "Technical alarms", desc: "Alarm management and maintenance support for technical installations." },
    ],
    archEyebrow: "Architecture",
    archTitle: "Edge → SCADA → Web.",
    archSteps: [
      { tag: "01", title: "Edge", desc: "PPC and IO controllers on dedicated industrial hardware, with deterministic cycle time and redundancy at the layer closest to field equipment." },
      { tag: "02", title: "SCADA", desc: "On-site SCADA server that acquires, processes and stores data across the entire plant, manages alarms and exposes the information to operators." },
      { tag: "03", title: "Web", desc: "Web service exposing SCADA in the browser — secure dashboards, KPIs and remote operation on any device, no local install required." },
    ]
  },
  pt: {
    heroEyebrow: "Produtos",
    heroTitle: <>Stack Integrado <span className="text-[#F68A31]">WSBP</span></>,
    heroSubtitle: "PPC, SCADA, BESS, JANUS e BMS numa única stack de engenharia — coordenando centrais fotovoltaicas, ativos híbridos, sistemas de armazenamento, subestações e edifícios em tempo real.",
    btnDemo: "Solicitar uma demo",
    btnTalkEng: "Falar com a engenharia",
    btnAccess: "Solicitar acesso",
    ppcTitle: "Power Plant Controller",
    ppcDesc: "A plataforma central de coordenação em tempo real. O PPC da WSBP cumpre rigorosamente os setpoints definidos pelos operadores de rede, garantindo a estabilidade da potência ativa, potência reativa, tensão e frequência.",
    ppcFeatures: [
      { icon: Zap, title: "Controlo P / f  &  Q / U", desc: "Potência ativa, frequência, potência reativa e tensão estáveis e alinhadas com os setpoints do operador." },
      { icon: ShieldCheck, title: "Operadores de Rede", desc: "Conformidade estrita com os requisitos dos operadores de rede em Portugal e na Europa." },
      { icon: Network, title: "Multi-protocolo", desc: "Modbus TCP/RTU, IEC 61850, IEC 60870, DNP3, OPC UA, entre outros." },
      { icon: Gauge, title: "Baixo tempo de resposta", desc: "Loop de controlo determinístico com latência reduzida, garantindo resposta rápida a variações da rede." },
    ],
    scadaTitle: "Sistemas SCADA",
    scadaDesc: "Soluções de supervisão personalizadas, projeto a projeto. Monitorização total de equipamentos de campo (inversores, trackers, estações meteorológicas), gestão de alarmes e análise de KPIs críticos como Performance Ratio, fator de capacidade e disponibilidade da central.",
    scadaFeatures: [
      { icon: Activity, title: "Monitorização Total", desc: "Inversores, trackers, estações meteorológicas e muito mais." },
      { icon: Database, title: "KPIs Críticos", desc: "Performance Ratio, fator de capacidade e disponibilidade da central em tempo real." },
      { icon: Radio, title: "Gestão de Alarmes", desc: "Correlação de eventos, prioridades e notificações via e-mail e SMS." },
      { icon: Cable, title: "À Medida", desc: "Soluções de supervisão desenhadas para os requisitos específicos de cada projeto." },
    ],
    bessTitle: "Armazenamento de Energia",
    bessDesc: "Estamos na vanguarda da integração de sistemas de armazenamento de energia em baterias (BESS) — entregámos o primeiro cluster de baterias stand-alone em Portugal. As nossas soluções de SCADA e controlo oferecem uma gestão completa do fluxo de potência, monitorização detalhada do Estado de Carga (SoC) e interação segura com o mercado elétrico em centrais híbridas.",
    bessFeatures: [
      { icon: BatteryCharging, title: "Gestão de SoC", desc: "Monitorização detalhada do Estado de Carga, temperatura, tensão e integridade (SoH) de cada string." },
      { icon: Gauge, title: "Fluxo de Potência", desc: "Controlo total de carga/descarga coordenado de forma inteligente com o PPC e o EMS." },
      { icon: Cpu, title: "Mercado Elétrico", desc: "Integração com o mercado diário (spot) e serviços de sistema/auxiliares (frequência, reserva de capacidade)." },
      { icon: ShieldCheck, title: "Operação Segura", desc: "Estratégias de proteção e redundância para garantir uma integração robusta e de última geração." },
    ],
    janusTitle: "JANUS",
    janusDesc: "Uma plataforma web para monitorização e análise em tempo real, focada em eficiência energética e sustentabilidade. O JANUS faz o acompanhamento de redes distribuídas de instalações, integra dados de sensores ambientais e de energia e gera relatórios automáticos que apoiam decisões operacionais e estratégias de manutenção a longo prazo.",
    janusFeatures: [
      { icon: LayoutDashboard, title: "Plataforma Web", desc: "Dashboards no navegador para locais distribuídos — sem necessidade de instalação local." },
      { icon: Leaf, title: "Eficiência Energética", desc: "Desenhado com base em KPIs de eficiência energética e sustentabilidade." },
      { icon: Bell, title: "Sensores e Alertas", desc: "Integra sensores ambientais e de energia com um sistema inteligente de alertas." },
      { icon: FileBarChart2, title: "Relatórios Automáticos", desc: "Relatórios periódicos e análises avançadas para as equipas de operação e gestão." },
    ],
    bmsTitle: "Gestão Técnica Centralizada",
    bmsDesc: "Gestão Técnica Centralizada (GTC/BMS) — a área onde a WSBP começou. Projetamos e integramos sistemas que supervisionam e controlam AVAC, iluminação, energia e alarmes técnicos em edifícios, com projetos entregues em aeroportos, hospitais, hotéis, escolas e instalações municipais em Portugal, Cabo Verde e Emirados Árabes Unidos.",
    bmsFeatures: [
      { icon: Fan, title: "Controlo AVAC", desc: "Supervisão e controlo de sistemas de ventilação, climatização e qualidade do ar." },
      { icon: Lightbulb, title: "Iluminação e Energia", desc: "Gestão de iluminação, contagem de energia e otimização de consumos." },
      { icon: LayoutDashboard, title: "Supervisão Centralizada", desc: "Todos os sistemas do edifício integrados e operados numa única plataforma." },
      { icon: Bell, title: "Alarmes Técnicos", desc: "Gestão de alarmes e apoio à manutenção das instalações técnicas." },
    ],
    archEyebrow: "Arquitetura",
    archTitle: "Edge → SCADA → Web.",
    archSteps: [
      { tag: "01", title: "Edge", desc: "Controladores PPC e IO em hardware industrial dedicado, com tempo de ciclo determinístico e redundância na camada mais próxima dos equipamentos de campo." },
      { tag: "02", title: "SCADA", desc: "Servidor SCADA local que adquire, processa e armazena dados de toda a central, faz a gestão de alarmes e disponibiliza a informação aos operadores." },
      { tag: "03", title: "Web", desc: "Serviço web que expõe o SCADA no browser — dashboards seguros, KPIs e operação remota em qualquer dispositivo, sem instalação local." },
    ]
  }
};

export default function Products() {
  const { hash } = useLocation();
  const { language } = useLanguage();
  
  // Âncoras de Scroll Inteligente (Smooth / Lenis)
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const run = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      if (window.__lenis) {
        window.__lenis.scrollTo(top, { immediate: false });
      } else {
        window.scrollTo({ top, behavior: "smooth" });
      }
    };
    const t = setTimeout(run, 120);
    return () => clearTimeout(t);
  }, [hash]);

  const t = TRANSLATIONS[language];

  return (
    <div data-testid="products-page">
      <PageHero
        eyebrow={t.heroEyebrow}
        title={t.heroTitle}
        subtitle={t.heroSubtitle}
        image="https://images.pexels.com/photos/21905739/pexels-photo-21905739.jpeg"
      />

      {/* PPC */}
      <section
        id="ppc"
        className="relative z-10 mx-auto max-w-[1400px] scroll-mt-24 px-6 py-6 md:px-12 lg:px-16"
        data-testid="product-ppc-section"
      >
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="inline-flex items-center gap-3 rounded-full border border-[#F68A31]/40 bg-[#F68A31]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#F68A31]">
                <Cpu size={14} /> PPC
              </div>
              <h2 className="font-display mt-6 text-4xl font-bold tracking-tight sm:text-6xl">
                {t.ppcTitle}
              </h2>
              <p className="mt-6 text-lg text-white/75">
                {t.ppcDesc}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary text-sm">
                  {t.btnDemo} <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <StaggerGroup className="grid gap-5 sm:grid-cols-2">
              {t.ppcFeatures.map((f, idx) => {
                const IconComponent = f.icon;
                return (
                  <StaggerItem
                    key={idx}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-[#F68A31]/50"
                  >
                    <IconComponent className="text-[#F68A31]" size={22} />
                    <h3 className="font-display mt-4 text-lg font-semibold">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-sm text-white/65">{f.desc}</p>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="divider-line" />
      </div>

      {/* SCADA */}
      <section
        id="scada"
        className="relative z-10 mx-auto max-w-[1400px] scroll-mt-24 px-6 py-24 md:px-12 lg:px-16"
        data-testid="product-scada-section"
      >
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          <div className="order-2 lg:order-1 lg:col-span-7">
            <StaggerGroup className="grid gap-5 sm:grid-cols-2">
              {t.scadaFeatures.map((f, idx) => {
                const IconComponent = f.icon;
                return (
                  <StaggerItem
                    key={idx}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-[#F68A31]/50"
                  >
                    <IconComponent className="text-[#F68A31]" size={22} />
                    <h3 className="font-display mt-4 text-lg font-semibold">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-sm text-white/65">{f.desc}</p>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          </div>
          <div className="order-1 lg:order-2 lg:col-span-5">
            <Reveal>
              <div className="inline-flex items-center gap-3 rounded-full border border-[#F68A31]/40 bg-[#F68A31]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#F68A31]">
                <LineChart size={14} /> SCADA
              </div>
              <h2 className="font-display mt-6 text-4xl font-bold tracking-tight sm:text-6xl">
                {t.scadaTitle}
              </h2>
              <p className="mt-6 text-lg text-white/75">
                {t.scadaDesc}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary text-sm">
                  {t.btnDemo} <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="divider-line" />
      </div>

      {/* BESS */}
      <section
        id="bess"
        className="relative z-10 mx-auto max-w-[1400px] scroll-mt-24 px-6 py-24 md:px-12 lg:px-16"
        data-testid="product-bess-section"
      >
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="inline-flex items-center gap-3 rounded-full border border-[#F68A31]/40 bg-[#F68A31]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#F68A31]">
                <BatteryCharging size={14} /> BESS
              </div>
              <h2 className="font-display mt-6 text-4xl font-bold tracking-tight sm:text-6xl">
                {t.bessTitle}
              </h2>
              <p className="mt-6 text-lg text-white/75">
                {t.bessDesc}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary text-sm">
                  {t.btnTalkEng} <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <StaggerGroup className="grid gap-5 sm:grid-cols-2">
              {t.bessFeatures.map((f, idx) => {
                const IconComponent = f.icon;
                return (
                  <StaggerItem
                    key={idx}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-[#F68A31]/50"
                  >
                    <IconComponent className="text-[#F68A31]" size={22} />
                    <h3 className="font-display mt-4 text-lg font-semibold">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-sm text-white/65">{f.desc}</p>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="divider-line" />
      </div>

      {/* JANUS */}
      <section
        id="janus"
        className="relative z-10 mx-auto max-w-[1400px] scroll-mt-24 px-6 py-24 md:px-12 lg:px-16"
        data-testid="product-janus-section"
      >
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          <div className="order-2 lg:order-1 lg:col-span-7">
            <StaggerGroup className="grid gap-5 sm:grid-cols-2">
              {t.janusFeatures.map((f, idx) => {
                const IconComponent = f.icon;
                return (
                  <StaggerItem
                    key={idx}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-[#F68A31]/50"
                  >
                    <IconComponent className="text-[#F68A31]" size={22} />
                    <h3 className="font-display mt-4 text-lg font-semibold">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-sm text-white/65">{f.desc}</p>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          </div>
          <div className="order-1 lg:order-2 lg:col-span-5">
            <Reveal>
              <div className="inline-flex items-center gap-3 rounded-full border border-[#F68A31]/40 bg-[#F68A31]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#F68A31]">
                <Activity size={14} /> JANUS
              </div>
              <h2 className="font-display mt-6 text-4xl font-bold tracking-tight sm:text-6xl">
                {t.janusTitle}
              </h2>
              <p className="mt-6 text-lg text-white/75">
                {t.janusDesc}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary text-sm">
                  {t.btnAccess} <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="divider-line" />
      </div>

      {/* BMS */}
      <section
        id="bms"
        className="relative z-10 mx-auto max-w-[1400px] scroll-mt-24 px-6 py-24 md:px-12 lg:px-16"
        data-testid="product-bms-section"
      >
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="inline-flex items-center gap-3 rounded-full border border-[#F68A31]/40 bg-[#F68A31]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#F68A31]">
                <Building2 size={14} /> BMS
              </div>
              <h2 className="font-display mt-6 text-4xl font-bold tracking-tight sm:text-6xl">
                {t.bmsTitle}
              </h2>
              <p className="mt-6 text-lg text-white/75">
                {t.bmsDesc}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary text-sm">
                  {t.btnTalkEng} <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <StaggerGroup className="grid gap-5 sm:grid-cols-2">
              {t.bmsFeatures.map((f, idx) => {
                const IconComponent = f.icon;
                return (
                  <StaggerItem
                    key={idx}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-[#F68A31]/50"
                  >
                    <IconComponent className="text-[#F68A31]" size={22} />
                    <h3 className="font-display mt-4 text-lg font-semibold">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-sm text-white/65">{f.desc}</p>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          </div>
        </div>
      </section>

      {/* ARCHITECTURE */}
      <section className="relative z-10 mx-auto max-w-[1400px] px-6 py-24 md:px-12 lg:px-16">
        <Reveal>
          <div className="eyebrow">{t.archEyebrow}</div>
          <h2 className="font-display mt-6 max-w-3xl text-4xl font-bold tracking-tight sm:text-5xl">
            {t.archTitle}
          </h2>
        </Reveal>
        <StaggerGroup className="mt-14 grid gap-6 lg:grid-cols-3">
          {t.archSteps.map((s) => (
            <StaggerItem
              key={s.tag}
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-8"
            >
              <div className="font-display text-5xl font-black text-[#F68A31]">
                {s.tag}
              </div>
              <h3 className="font-display mt-4 text-2xl font-semibold">
                {s.title}
              </h3>
              <p className="mt-3 text-white/65">{s.desc}</p>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>
    </div>
  );
}